class EasycandleGifts extends HTMLElement {
  constructor() {
    super();

    this.moneyFormat = window.FoxTheme.settings.moneyFormat;
    this.strings = window.FoxTheme.easycandleGiftStrings || {};
    const rate = Number((window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1);
    this.gifts = (this.readJSON('EasycandleGiftOptions') || []).map((gift) => ({
      ...gift,
      // Round the converted threshold up to a whole currency unit so it reads as a clean
      // marketing number (22.49 EUR -> 23 EUR) instead of an odd converted amount.
      thresholdCents: Math.ceil((gift.thresholdCents * rate) / 100) * 100,
      priceCents: Math.round(gift.priceCents * rate),
    }));

    this.banner = this.querySelector('[data-easycandle-banner]');
    this.progressLabel = this.querySelector('[data-easycandle-progress-label]');
    this.doneLabel = this.querySelector('[data-easycandle-done-label]');
    this.upsellLabel = this.querySelector('[data-easycandle-upsell-label]');
    this.progressBar = this.querySelector('progress-bar');
    this.picker = this.querySelector('[data-easycandle-picker]');
    this.pickerHeading = this.querySelector('[data-easycandle-picker-heading]');
    this.pickerList = this.querySelector('[data-easycandle-picker-list]');
    this.optionTemplate = document.getElementById('EasycandleGiftOptionTemplate');

    this.tiers = this.groupByThreshold(this.gifts);
    this.isSyncing = false;
    this.onCartUpdate = this.onCartUpdate.bind(this);
  }

  connectedCallback() {
    if (!this.gifts.length) return;

    this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate);

    fetch(`${FoxTheme.routes.cart_url}.js`)
      .then((response) => response.json())
      .then((cart) => this.evaluate(cart))
      .catch((error) => console.error('easyCandle gifts:', error));
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
  }

  onCartUpdate(event) {
    if (event.cart && !event.cart.errors) this.evaluate(event.cart);
  }

  readJSON(id) {
    const node = document.getElementById(id);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.error(`easyCandle gifts: could not parse #${id}`, error);
      return null;
    }
  }

  groupByThreshold(gifts) {
    const byThreshold = new Map();
    gifts.forEach((gift) => {
      const group = byThreshold.get(gift.thresholdCents) || [];
      group.push(gift);
      byThreshold.set(gift.thresholdCents, group);
    });
    return Array.from(byThreshold.entries())
      .map(([thresholdCents, gifts]) => ({thresholdCents, gifts}))
      .sort((a, b) => b.thresholdCents - a.thresholdCents);
  }

  evaluate(cart) {
    const items = cart.items || [];
    const giftItems = items.filter((item) => item.properties && item.properties._easycandle_gift);
    const giftLinesTotal = giftItems.reduce((sum, item) => sum + item.final_line_price, 0);

    const qualifyingSubtotal = (cart.items_subtotal_price || 0) - giftLinesTotal;
    const activeTiers = this.tiers.filter((tier) => qualifyingSubtotal >= tier.thresholdCents);
    const activeVariantIds = new Set(activeTiers.flatMap((tier) => tier.gifts.map((gift) => String(gift.id))));

    this.renderBanner(qualifyingSubtotal, activeTiers);

    const eligibleItems = giftItems.filter((item) => activeVariantIds.has(String(item.variant_id)));
    const existingItem = eligibleItems[0] || null;

    this.renderPicker(activeTiers, existingItem);

    if (this.isSyncing) return;

    const overQuantityItem = giftItems.find((item) => item.quantity > 1);
    if (overQuantityItem) {
      this.setGiftQuantity(overQuantityItem.key, 1);
      return;
    }

    const staleItem = giftItems.find((item) => !activeVariantIds.has(String(item.variant_id)));
    if (staleItem) {
      this.removeGiftLine(staleItem.key);
      return;
    }

    if (eligibleItems.length > 1) {
      this.removeGiftLine(eligibleItems[1].key);
    }
  }

  renderBanner(qualifyingSubtotal, activeTiers) {
    if (!this.banner) return;
    if (!this.tiers.length) {
      this.banner.hidden = true;
      return;
    }

    this.banner.hidden = false;

    const unmetTiers = this.tiers.filter((tier) => qualifyingSubtotal < tier.thresholdCents);
    const nextTier = unmetTiers.length ? unmetTiers[unmetTiers.length - 1] : null;

    if (activeTiers.length) {
      this.progressLabel.hidden = true;
      this.doneLabel.hidden = false;
      this.doneLabel.innerHTML = (this.strings.congratulations_html || '').replace('[gift]', this.tierLabel(activeTiers[0]));
      this.progressBar.style.setProperty('--percent', '100%');

      if (this.upsellLabel) {
        if (nextTier) {
          const remaining = nextTier.thresholdCents - qualifyingSubtotal;
          this.upsellLabel.hidden = false;
          this.upsellLabel.innerHTML = (this.strings.upsell_html || '').replace(
            '[remaining_amount]',
            `<strong>${FoxTheme.Currency.formatMoney(remaining, this.moneyFormat)}</strong>`,
          );
        } else {
          this.upsellLabel.hidden = true;
        }
      }
      return;
    }

    if (this.upsellLabel) this.upsellLabel.hidden = true;

    if (!nextTier) {
      this.banner.hidden = true;
      return;
    }

    const remaining = nextTier.thresholdCents - qualifyingSubtotal;
    this.progressLabel.hidden = false;
    this.doneLabel.hidden = true;
    this.progressLabel.innerHTML = (this.strings.remaining_html || '')
      .replace('[remaining_amount]', FoxTheme.Currency.formatMoney(remaining, this.moneyFormat))
      .replace('[gift]', this.tierLabel(nextTier));
    const percent = Math.min((qualifyingSubtotal / nextTier.thresholdCents) * 100, 100);
    this.progressBar.style.setProperty('--percent', `${percent}%`);
    this.progressBar.dataset.value = qualifyingSubtotal;
    this.progressBar.dataset.max = nextTier.thresholdCents;
  }

  tierLabel(tier) {
    return tier.gifts.length === 1 ? tier.gifts[0].title : this.strings.choice_label || '';
  }

  renderPicker(activeTiers, existingItem) {
    if (!this.picker || !this.pickerList) return;

    if (!activeTiers.length) {
      this.picker.hidden = true;
      this.pickerList.innerHTML = '';
      return;
    }

    const allGifts = activeTiers.flatMap((tier) => tier.gifts);

    this.picker.hidden = false;
    if (this.pickerHeading) {
      this.pickerHeading.textContent =
        allGifts.length === 1 ? this.strings.single_heading || '' : this.strings.choice_heading || '';
    }
    this.pickerList.innerHTML = '';

    allGifts.forEach((gift) => {
      if (!this.optionTemplate) return;

      const node = this.optionTemplate.content.firstElementChild.cloneNode(true);
      const image = node.querySelector('[data-easycandle-option-image]');
      const title = node.querySelector('[data-easycandle-option-title]');
      const price = node.querySelector('[data-easycandle-option-price]');
      const button = node.querySelector('[data-easycandle-option-button]');

      const isSelected = !!(existingItem && String(existingItem.variant_id) === String(gift.id));
      if (gift.image) image.style.backgroundImage = `url(${gift.image})`;
      if (title) title.textContent = gift.variantTitle && gift.variantTitle !== 'Default Title' ? `${gift.title} — ${gift.variantTitle}` : gift.title;
      if (price) {
        price.textContent =
          gift.priceCents <= 0
            ? this.strings.free_label || ''
            : FoxTheme.Currency.formatMoney(gift.priceCents, this.moneyFormat);
      }
      if (button) {
        // The label must live in a .btn__text span, not straight on the button, so it keeps
        // the theme's z-index:1 stacking above the ::before hover-fill overlay (see .btn
        // hover rules in theme.css) - otherwise the text disappears under it on hover.
        const buttonText = button.querySelector('[data-easycandle-option-button-text]');
        const label = isSelected ? this.strings.remove_label || '' : this.strings.add_label || '';
        if (buttonText) {
          buttonText.textContent = label;
        } else {
          button.textContent = label;
        }
        button.classList.toggle('btn--secondary', isSelected);
        button.classList.toggle('btn--primary', !isSelected);
        button.addEventListener('click', () => this.toggleGift(gift.id, existingItem));
      }

      this.pickerList.appendChild(node);
    });
  }

  toggleGift(variantId, existingItem) {
    if (this.isSyncing) return;

    if (existingItem && String(existingItem.variant_id) === String(variantId)) {
      this.removeGiftLine(existingItem.key);
      return;
    }

    const removal = existingItem ? this.removeGiftLine(existingItem.key) : Promise.resolve();
    removal.then(() => this.addGiftThenSync(variantId));
  }

  addGiftThenSync(variantId) {
    this.isSyncing = true;
    return fetch(FoxTheme.routes.cart_add_url, {
      ...FoxTheme.utils.fetchConfig(),
      body: JSON.stringify({
        items: [{id: variantId, quantity: 1, properties: {_easycandle_gift: 'true'}}]
      })
    })
      .then((response) => response.json())
      .then((added) => {
        if (added.status) throw new Error(added.description || 'Unable to add gift');
        return this.syncCart();
      })
      .catch((error) => console.error('easyCandle gifts:', error))
      .finally(() => {
        this.isSyncing = false;
      });
  }

  removeGiftLine(lineKey) {
    return this.setGiftQuantity(lineKey, 0);
  }

  setGiftQuantity(lineKey, quantity) {
    this.isSyncing = true;
    return fetch(FoxTheme.routes.cart_change_url, {
      ...FoxTheme.utils.fetchConfig(),
      body: JSON.stringify({id: lineKey, quantity, sections: this.getSectionsToUpdate()})
    })
      .then((response) => response.json())
      .then((parsedState) => this.publishCartUpdate(parsedState))
      .catch((error) => console.error('easyCandle gifts:', error))
      .finally(() => {
        this.isSyncing = false;
      });
  }

  syncCart() {
    return fetch(FoxTheme.routes.cart_update_url, {
      ...FoxTheme.utils.fetchConfig(),
      body: JSON.stringify({sections: this.getSectionsToUpdate()})
    })
      .then((response) => response.json())
      .then((parsedState) => this.publishCartUpdate(parsedState));
  }

  getSectionsToUpdate() {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', {bubbles: true, detail: {sections}})
    );
    return sections;
  }

  publishCartUpdate(cart) {
    FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {cart});
  }
}
customElements.define('easycandle-gifts', EasycandleGifts);
